import React, { useEffect } from 'react';
import { Toaster as Sonner, toast, type ToasterProps } from 'sonner';
import { useTheme } from '../../hooks/useTheme';

/**
 * Dev-only styling aid: fire one persistent toast of every variant so the
 * styles can be tuned live. Exposed on `window.__fireAllToasts()` (and the raw
 * `window.__toast`) in development only — never wired into product UI.
 */
function fireAllToasts() {
  const duration = Infinity; // stay put while styling
  toast('Default toast', { description: 'A plain message with a longer supporting description underneath.', duration });
  toast.success('Saved your changes', { description: 'The bot config was updated.', duration });
  toast.error("Couldn't play this recording", { description: 'The audio file is missing or in an unsupported format.', duration });
  toast.warning('You have unsaved changes', { description: 'Leaving now will discard them.', duration });
  toast.info('3 new calls since you last looked', { duration });
  toast.loading('Uploading recording…', { duration });
  toast('Contact removed', {
    description: 'They were taken off the campaign.',
    duration,
    action: { label: 'Undo', onClick: () => {} },
    cancel: { label: 'Dismiss', onClick: () => {} },
  });
}

/**
 * shadcn Sonner wrapper. Themed off the app's own `useTheme` (not next-themes)
 * and mapped to the soft-theme CSS tokens so toasts match both light and dark.
 * The `--normal-*` vars feed sonner's default toast; `richColors` gives error/
 * success toasts their own accent styling.
 */
function Toaster(props: ToasterProps) {
  const { resolvedTheme } = useTheme();

  // Dev-only console handles for styling toasts live.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const w = window as unknown as { __toast?: typeof toast; __fireAllToasts?: () => void };
    w.__toast = toast;
    w.__fireAllToasts = fireAllToasts;
  }, []);

  return (
    <Sonner
      theme={resolvedTheme}
      richColors
      closeButton
      expand
      visibleToasts={9}
      className="toaster group"
      style={
        {
          '--normal-bg': 'var(--paper, var(--color-gray-100))',
          '--normal-text': 'var(--ink, var(--text-default))',
          '--normal-border': 'var(--line-2, var(--border-color))',
        } as React.CSSProperties
      }
      {...props}
    />
  );
}

export { Toaster };
